import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosPromise
} from "axios";
import camelCaseKeys from "camelcase-keys";
import {
  BackSideMeishiList,
  Meishi,
  Paper,
  VacationInformation,
  LogoFile,
  mapMeishi
} from "@/gateway/response/api";
import { NewInterface } from "@/model/views/New.ts";
import { RepeatInterface } from "@/model/views/Repeat";
import { ManuscriptInterface } from "@/model/views/Manuscript";
import { InquiryInterface } from "@/model/views/inquiry/Inquiry";
import { OrderPrice } from "@/model/price";
import newOrderRequest from "@/gateway/request/new.ts";
import repeatOrderRequest from "@/gateway/request/repeat.ts";
import manuscriptOrderRequest from "@/gateway/request/manuscript.ts";
import inquiryOrderRequest from "@/gateway/request/inquiry.ts";

const client: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest"
  }
});

export default {
  fetchMeishiByDesignNo(designNo: number): Promise<Meishi> {
    return client.get(`/get/meishi/design_no/${designNo}`).then(response => {
      return mapMeishi(response.data.payload);
    });
  },
  fetchBackSideMeishiList(): Promise<BackSideMeishiList> {
    return client.get("/get/meishi/backside").then(response => {
      const p = response.data.payload;

      return {
        color: p.color.map((m: Meishi) => mapMeishi(m)),
        monochrome: p.monochrome.map((m: Meishi) => mapMeishi(m))
      };
    });
  },
  fetchPaperList(): Promise<Paper[]> {
    return client.get("/get/papers").then(response => response.data.payload);
  },
  fetchVacationInformation(): Promise<VacationInformation> {
    return client
      .get("/get/site/vacation")
      .then(response => response.data.payload);
  },

  validateNewOrder(f: NewInterface, o: OrderPrice): Promise<AxiosResponse> {
    const req = newOrderRequest.map(f, o);

    return client.post("/new/validation", req).then(response => response.data);
  },
  requestNewOrder(f: NewInterface, o: OrderPrice): Promise<AxiosResponse> {
    const req = newOrderRequest.map(f, o);

    return client.post("/new", req).then(response => response.data);
  },
  validateRepeatOrder(f: RepeatInterface): Promise<AxiosResponse> {
    const req = repeatOrderRequest.map(f);

    return client
      .post("/repeat/validation", req)
      .then(response => response.data);
  },
  requestRepeatOrder(
    f: RepeatInterface,
    o: OrderPrice
  ): Promise<AxiosResponse> {
    const req = repeatOrderRequest.map(f, o);

    return client.post("/repeat", req).then(response => response.data);
  },
  validateManuscriptOrder(f: ManuscriptInterface, o: OrderPrice) {
    const req = manuscriptOrderRequest.map(f, o);

    return client
      .post("/manuscript/validation", req)
      .then(response => response.data);
  },
  requestManuscriptOrder(
    f: ManuscriptInterface,
    o: OrderPrice
  ): Promise<AxiosResponse> {
    const req = manuscriptOrderRequest.map(f, o);

    return client.post("/manuscript", req).then(response => response.data);
  },
  validateInquiryOrder(f: InquiryInterface) {
    const req = inquiryOrderRequest.map(f);

    return client
      .post("/inquiry/validation", req)
      .then(response => response.data);
  },
  requestInquiryRequest(f: InquiryInterface): Promise<AxiosResponse> {
    const req = inquiryOrderRequest.map(f);

    return client.post("/inquiry", req).then(response => response.data);
  },

  upload(file: File): Promise<LogoFile> {
    const f = new FormData();
    f.append("file", file);

    return client
      .post("/files", f, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })
      .then(response => response.data);
  }
};

client.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    response.data = camelCaseKeys(response.data, { deep: true });

    return response;
  },
  (error: AxiosError): AxiosPromise => {
    alert(
      "サーバとの通信に失敗しました。お手数ですがWebブラウザの再読み込みをしてください。"
    );
    return Promise.reject(error);
  }
);
